/**
 * useSignupRecovery — post-login safety net for broken onboarding.
 *
 * If the `handle-new-user` edge function failed silently (e.g. pg_net hiccup),
 * the user can end up in auth.users with NO company_members / user_roles rows.
 * This hook detects that state and re-invokes the same edge function, which is
 * already idempotent (checks for existing role/member before inserting).
 *
 * Guarantees:
 *  - Runs at most ONCE per authenticated session (per user ID).
 *  - Never runs for unauthenticated users.
 *  - Never creates duplicate rows (edge function handles idempotency).
 *  - Does not alter any existing signup, trigger, or subscription logic.
 */

import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { toast } from "sonner";

export function useSignupRecovery(session: Session | null) {
  const attemptedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session?.user?.id) return;

    const userId = session.user.id;

    // Only attempt once per user per session
    if (attemptedForUserRef.current === userId) return;
    attemptedForUserRef.current = userId;

    let cancelled = false;

    const runRecoveryCheck = async () => {
      try {
        // Quick check: does the user have a company_members row?
        const { data: membership, error: memberErr } = await supabase
          .from("company_members")
          .select("id")
          .eq("user_id", userId)
          .order("is_owner", { ascending: false })
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (memberErr) {
          // Network/RLS error — don't block the user, just bail
          console.warn("[SignupRecovery] Error checking membership, skipping:", memberErr.message);
          return;
        }

        if (membership) {
          // User has a membership — also verify user_roles exists
          const { data: role, error: roleErr } = await supabase
            .from("user_roles")
            .select("id")
            .eq("user_id", userId)
            .maybeSingle();

          if (roleErr) {
            console.warn("[SignupRecovery] Error checking role, skipping:", roleErr.message);
            return;
          }

          if (role) {
            // Both exist — user is healthy, nothing to do
            return;
          }
        }

        // Missing membership and/or role — attempt recovery
        console.warn("[SignupRecovery] Broken onboarding detected for user:", userId, "membership:", !!membership);

        if (cancelled) return;

        // Re-invoke the same handle-new-user edge function.
        // It already checks for existing role/member and skips if present.
        const { data: authUser } = await supabase.auth.getUser();
        if (!authUser?.user || cancelled) return;

        const user = authUser.user;
        const payload = {
          record: {
            id: user.id,
            email: user.email,
            raw_user_meta_data: user.user_metadata,
          },
        };

        console.log("[SignupRecovery] Invoking handle-new-user for recovery…");

        const { data, error } = await supabase.functions.invoke("handle-new-user", {
          body: payload,
        });

        if (cancelled) return;

        if (error) {
          console.error("[SignupRecovery] Recovery failed:", error.message);
          toast.error(
            "We had trouble setting up your account. Please try logging out and back in, or contact support if the problem persists.",
            { duration: 10000 }
          );
          return;
        }

        if (data?.success) {
          console.log("[SignupRecovery] Edge function returned success:", data.message);

          // Re-verify that the MISSING pieces actually got created before reloading.
          // The edge function is idempotent and answers `success` even when it
          // deliberately skips a user, so checking membership alone caused an
          // endless reload loop for accounts that hold a membership but no role
          // (e.g. someone who opened the Demo Company before onboarding ran).
          const [{ data: verifyMembership }, { data: verifyRole }] = await Promise.all([
            supabase
              .from("company_members")
              .select("id")
              .eq("user_id", userId)
              .order("is_owner", { ascending: false })
              .order("created_at", { ascending: true })
              .limit(1)
              .maybeSingle(),
            supabase.from("user_roles").select("id").eq("user_id", userId).maybeSingle(),
          ]);

          if (cancelled) return;

          if (verifyMembership && verifyRole) {
            console.log("[SignupRecovery] ✅ Recovery confirmed — membership and role now exist. Reloading.");
            toast.success("Account setup completed! Refreshing…", { duration: 3000 });
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } else {
            console.log("[SignupRecovery] Recovery ran but setup is still incomplete — skipping reload to avoid a loop.");
          }
        } else {
          console.log("[SignupRecovery] Edge function returned non-success:", data);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error("[SignupRecovery] Unexpected error:", err?.message);
        // Don't toast on unexpected errors — the user may still be able to use the app
      }
    };

    runRecoveryCheck();

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);
}
